import React, { useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Wallet,
  BarChart3,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { useDashboardPnl } from '../hooks/useDashboardPnl';
import { useAuth } from '../hooks/useAuth';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useDashboardFilter } from '../contexts/useDashboardFilterHooks';
import { formatCompactCurrency } from '../lib/calculations';
import {
  PnlTrendChart,
  ProjectProfitChart,
  CostBreakdownTreemap,
  ProfitMarginRadial,
  ParetoAnalysisChart,
  PnlChartAnimationStyles,
} from './PnlDashboardCharts';
import { PnlDashboardInsight } from './PnlDashboardInsight';
import './PnlDashboardCharts.css';
import { HowWorkedOut } from './howWorkedOut/HowWorkedOut';
import type { MetricDefinition, RightNowValue } from './howWorkedOut/metricDefinition';
import { PNL_DASHBOARD_FIGURES } from '../hooks/useDashboardPnl.metrics';

// ─────────────────────────────────────────────────────────────────────────────
// Animation keyframes for metric cards
// ─────────────────────────────────────────────────────────────────────────────

const metricAnimationStyles = `
@keyframes metricSlideIn {
  from { opacity: 0; transform: translateY(12px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.pnl-metric-card {
  animation: metricSlideIn 0.4s ease-out both;
}
.pnl-metric-card:nth-child(1) { animation-delay: 0s; }
.pnl-metric-card:nth-child(2) { animation-delay: 0.06s; }
.pnl-metric-card:nth-child(3) { animation-delay: 0.12s; }
.pnl-metric-card:nth-child(4) { animation-delay: 0.18s; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton Loader
// ─────────────────────────────────────────────────────────────────────────────

function PnlSectionSkeleton() {
  const shimmer: React.CSSProperties = {
    background: 'linear-gradient(90deg, var(--bg-tertiary, #f3f4f6) 25%, var(--bg-secondary, #e5e7eb) 50%, var(--bg-tertiary, #f3f4f6) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: 12,
  };

  return (
    <div>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      {/* Metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} style={{ ...shimmer, height: 100   }} />
        ))}
      </div>
      {/* Chart placeholders */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 20, marginBottom: 24 }}>
        <div style={{ ...shimmer, height: 320 }} />
        <div style={{ ...shimmer, height: 320 }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Metric Card
// ─────────────────────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string;
  subLabel?: string;
  icon: React.ReactNode;
  color: string;
  bgGradient: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  /** SEQ-240: the "How is this worked out" pop-up, replacing the old hover title. */
  definition: MetricDefinition;
  rightNow?: readonly RightNowValue[];
  /** Added to the trigger's accessible name where the same figure name appears elsewhere on the page. */
  where?: string;
}

function MetricCard({ label, value, subLabel, icon, color, bgGradient, trend, trendValue, definition, rightNow, where }: MetricCardProps) {
  return (
    <div
      className="card pnl-metric-card"
      style={{
        padding: '1.25rem',
        background: bgGradient,
        border: `1px solid ${color}20`,
        borderRadius: 16,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
        (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 24px ${color}15`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span className="hwo-heading">
          <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            {label}
          </span>
          <HowWorkedOut definition={definition} rightNow={rightNow} where={where} />
        </span>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
          }}
        >
          {icon}
        </div>
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary, #111827)', lineHeight: 1.2 }}>
        {value}
      </div>
      {(subLabel || trendValue) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
          {trend && (
            <span style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              color: trend === 'up' ? '#10b981' : trend === 'down' ? '#ef4444' : '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              background: trend === 'up' ? '#ecfdf5' : trend === 'down' ? '#fef2f2' : '#f3f4f6',
              padding: '2px 8px',
              borderRadius: 20,
            }}>
              {trend === 'up' ? <TrendingUp size={12} /> : trend === 'down' ? <TrendingDown size={12} /> : null}
              {trendValue}
            </span>
          )}
          {subLabel && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)' }}>
              {subLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Section
// ─────────────────────────────────────────────────────────────────────────────

export function PnlDashboardSection() {
  const { profile, loading: authLoading } = useAuth();
  const hasFinancialAccess = Boolean(profile?.can_view_financials);
  const { isSafeMode } = useSafeMode();
  const pnlData = useDashboardPnl();
  const { effectiveProjectIds, isFiltered, filters } = useDashboardFilter();

  // ── Client-side filtering of PnL data ──────────────────────────────────
  const filteredPnl = useMemo(() => {
    if (!isFiltered || !effectiveProjectIds) return pnlData;

    const idSet = new Set(effectiveProjectIds);
    const hasDateFilter = filters.datePreset !== 'all_time';
    const { dateRange } = filters;

    // Filter project summaries
    const filteredPerProject = pnlData.perProjectPnl.filter(p => idSet.has(p.projectId));
    const filteredMonthlyData = pnlData.projectMonthlyData.filter(p => idSet.has(p.projectId));

    // Recalculate monthly trend from filtered project monthly data
    const trendMap = new Map<string, { label: string; month: number; year: number; income: number; costs: number; profit: number; profitPercent: number }>();
    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    for (const pmd of filteredMonthlyData) {
      for (const mr of pmd.monthlyResults) {
        const parts = mr.key.split('-');
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]);

        // Apply date range filter to monthly results
        if (hasDateFilter) {
          const key = `${year}-${String(month).padStart(2, '0')}`;
          const startKey = `${dateRange.startYear}-${String(dateRange.startMonth).padStart(2, '0')}`;
          const endKey = `${dateRange.endYear}-${String(dateRange.endMonth).padStart(2, '0')}`;
          if (key < startKey || key > endKey) continue;
        }

        const existing = trendMap.get(mr.key);
        if (existing) {
          existing.income += mr.result.constructionIncome;
          existing.costs += mr.result.totalDeductions;
          existing.profit += mr.result.netProfit;
        } else {
          trendMap.set(mr.key, {
            label: `${MONTH_NAMES[month - 1]} ${year}`,
            month, year,
            income: mr.result.constructionIncome,
            costs: mr.result.totalDeductions,
            profit: mr.result.netProfit,
            profitPercent: 0,
          });
        }
      }
    }
    const monthlyTrend = [...trendMap.values()]
      .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month)
      .map(t => ({ ...t, profitPercent: t.income > 0 ? (t.profit / t.income) * 100 : 0 }));

    // When date filter is active, recalculate aggregates from monthly data (time-filtered)
    // Otherwise use all-time project summaries
    let totalIncome: number;
    let totalCosts: number;
    let totalProfit: number;
    let totalProfitPercent: number;
    let totalRetention: number;
    let totalMaterials = 0, totalWages = 0, totalOther = 0;

    if (hasDateFilter) {
      // Aggregate from time-filtered monthly trend data
      totalIncome = monthlyTrend.reduce((s, m) => s + m.income, 0);
      totalCosts = monthlyTrend.reduce((s, m) => s + m.costs, 0);
      totalProfit = totalIncome - totalCosts;
      totalProfitPercent = totalIncome > 0 ? (totalProfit / totalIncome) * 100 : 0;
      // Retention is cumulative (not time-filtered), still from project summaries
      totalRetention = filteredPerProject.reduce((s, p) => s + p.retention, 0);
      // Cost breakdown from monthly data isn't available at granular level,
      // so we keep project-level proportions but scale to the filtered total
      const allTimeTotal = filteredPerProject.reduce((s, p) => s + p.totalDeductions, 0);
      for (const s of filteredPerProject) {
        totalMaterials += s.materialsCost;
        totalWages += s.wagesCost;
        totalOther += s.otherCosts;
      }
      if (allTimeTotal > 0 && totalCosts > 0) {
        const scale = totalCosts / allTimeTotal;
        totalMaterials = totalMaterials * scale;
        totalWages = totalWages * scale;
        totalOther = totalOther * scale;
      }
    } else {
      totalIncome = filteredPerProject.reduce((s, p) => s + p.constructionIncome, 0);
      totalCosts = filteredPerProject.reduce((s, p) => s + p.totalDeductions, 0);
      totalProfit = totalIncome - totalCosts;
      totalProfitPercent = totalIncome > 0 ? (totalProfit / totalIncome) * 100 : 0;
      totalRetention = filteredPerProject.reduce((s, p) => s + p.retention, 0);
      for (const s of filteredPerProject) {
        totalMaterials += s.materialsCost;
        totalWages += s.wagesCost;
        totalOther += s.otherCosts;
      }
    }

    const costBreakdown = [
      { name: 'Materials', value: totalMaterials, color: '#f59e0b' },
      { name: 'Wages', value: totalWages, color: '#3b82f6' },
      { name: 'Other', value: totalOther, color: '#8b5cf6' },
    ];

    return {
      ...pnlData,
      perProjectPnl: filteredPerProject,
      projectMonthlyData: filteredMonthlyData,
      totalIncome, totalCosts, totalProfit, totalProfitPercent, totalRetention,
      costBreakdown,
      monthlyTrend,
    };
  }, [pnlData, effectiveProjectIds, isFiltered, filters]);

  // Security: Same access control as P&L tab, completely hidden for unauthorized users
  // Safe Mode: Only the Pareto Analysis (patching) is shown; all other financial
  // charts and metric cards are hidden. Dollar values within Pareto are auto-masked
  // by formatCurrency, so Tom can discuss defect ratios without exposing financials.
  if (authLoading) return null;
  if (!hasFinancialAccess) return null;

  // Error state
  if (pnlData.error && !pnlData.loading) {
    return (
      <div style={{ marginBottom: 'var(--spacing-xl, 2rem)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary, #111827)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart3 size={20} style={{ color: '#7c3aed' }} />
            Financial P&L Overview
          </h3>
        </div>
        <div className="card" style={{ padding: '2rem', textAlign: 'center', borderRadius: 16 }}>
          <AlertTriangle size={32} style={{ color: '#f59e0b', marginBottom: 12 }} />
          <p style={{ color: 'var(--text-secondary, #6b7280)', marginBottom: 12 }}>{pnlData.error}</p>
          <button onClick={pnlData.refresh} className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-tour-id="pnl-section" style={{ marginBottom: 'var(--spacing-xl, 2rem)' }}>
      {/* Inject animation styles */}
      <style>{metricAnimationStyles}</style>
      <PnlChartAnimationStyles />

      {/* Section Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary, #111827)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <BarChart3 size={20} style={{ color: isSafeMode ? '#f97316' : '#7c3aed' }} />
          {isSafeMode ? 'Patching Analysis' : 'Financial P&L Overview'}
          {isFiltered && !isSafeMode && (
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-muted, #9ca3af)', marginLeft: 4 }}>
              ({filteredPnl.perProjectPnl.length} projects)
            </span>
          )}
        </h3>
        {!pnlData.loading && !isSafeMode && (
          <button
            onClick={pnlData.refresh}
            className="btn btn-ghost btn-sm"
            style={{ color: 'var(--text-muted, #9ca3af)', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        )}
      </div>

      {pnlData.loading ? (
        <PnlSectionSkeleton />
      ) : (
        <>
          {/* ────────── Row 1: Metric Cards (hidden in Safe Mode) ────────── */}
          {!isSafeMode && (
          <div className="pnl-grid-row pnl-grid-metrics">
            <MetricCard
              label="Total Income"
              value={formatCompactCurrency(filteredPnl.totalIncome)}
              subLabel={`${filteredPnl.perProjectPnl.length} projects`}
              icon={<DollarSign size={18} />}
              color="#3b82f6"
              bgGradient="linear-gradient(135deg, rgba(59,130,246,0.04) 0%, rgba(59,130,246,0.01) 100%)"
              definition={PNL_DASHBOARD_FIGURES["pnl.total-income"]}
              rightNow={[
                { label: 'Total income', value: filteredPnl.totalIncome, unit: 'currency' },
                { label: 'Projects', value: filteredPnl.perProjectPnl.length, unit: 'count' },
              ]}
            />
            <MetricCard
              label="Total Costs"
              value={formatCompactCurrency(filteredPnl.totalCosts)}
              subLabel="All deductions"
              icon={<Wallet size={18} />}
              color="#ef4444"
              bgGradient="linear-gradient(135deg, rgba(239,68,68,0.04) 0%, rgba(239,68,68,0.01) 100%)"
              definition={PNL_DASHBOARD_FIGURES["pnl.total-costs"]}
              rightNow={[{ label: 'Total costs', value: filteredPnl.totalCosts, unit: 'currency' }]}
            />
            <MetricCard
              label="Net Profit"
              value={formatCompactCurrency(filteredPnl.totalProfit)}
              trend={filteredPnl.totalProfit >= 0 ? 'up' : 'down'}
              trendValue={`${filteredPnl.totalProfitPercent.toFixed(1)}% margin`}
              icon={filteredPnl.totalProfit >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
              color={filteredPnl.totalProfit >= 0 ? '#10b981' : '#ef4444'}
              bgGradient={
                filteredPnl.totalProfit >= 0
                  ? 'linear-gradient(135deg, rgba(16,185,129,0.04) 0%, rgba(16,185,129,0.01) 100%)'
                  : 'linear-gradient(135deg, rgba(239,68,68,0.04) 0%, rgba(239,68,68,0.01) 100%)'
              }
              definition={PNL_DASHBOARD_FIGURES["pnl.net-profit"]}
              where="in the P&L overview"
              rightNow={[
                { label: 'Total income', value: filteredPnl.totalIncome, unit: 'currency' },
                { label: 'Total costs', value: filteredPnl.totalCosts, unit: 'currency' },
                { label: 'Net profit', value: filteredPnl.totalProfit, unit: 'currency' },
                { label: 'Margin', value: filteredPnl.totalProfitPercent, unit: 'percent' },
              ]}
            />
            <MetricCard
              label="Held less received"
              value={formatCompactCurrency(filteredPnl.totalRetention)}
              subLabel="Open retention"
              icon={<DollarSign size={18} />}
              color="#f59e0b"
              bgGradient="linear-gradient(135deg, rgba(245,158,11,0.04) 0%, rgba(245,158,11,0.01) 100%)"
              definition={PNL_DASHBOARD_FIGURES["pnl.retention-held"]}
              where="in the P&L overview"
              rightNow={[{ label: 'Open retention', value: filteredPnl.totalRetention, unit: 'currency' }]}
            />
          </div>
          )}

          {/* ────────── Row 2: Trend + Profit Comparison (hidden in Safe Mode) ────────── */}
          {!isSafeMode && (
          <div className="pnl-grid-row pnl-grid-2col">
            <PnlTrendChart data={filteredPnl.monthlyTrend} />
            <ProjectProfitChart data={filteredPnl.perProjectPnl} />
          </div>
          )}

          {/* ────────── Row 3: Cost Breakdown + Margins (hidden in Safe Mode) ────────── */}
          {!isSafeMode && (
          <div className="pnl-grid-row pnl-grid-2col">
            <CostBreakdownTreemap data={filteredPnl.costBreakdown} />
            <ProfitMarginRadial data={filteredPnl.perProjectPnl} />
          </div>
          )}

          {/* ────────── Row 4: Pareto Analysis (always visible, patching only in Safe Mode) ────────── */}
          <div style={{ marginBottom: 24 }}>
            <ParetoAnalysisChart
              data={filteredPnl.perProjectPnl}
              costBreakdown={filteredPnl.costBreakdown}
              projectMonthlyData={filteredPnl.projectMonthlyData}
              patchingOnly={isSafeMode}
            />
          </div>

          {/* ────────── Row 5: AI Insight (hidden in Safe Mode) ────────── */}
          {!isSafeMode && <PnlDashboardInsight pnlData={filteredPnl} />}
        </>
      )}
    </div>
  );
}
